import {orderBy, union} from 'lodash';
import moment from 'moment';
import {CONTENT_TYPES} from '../constants/trustContent';

const getPgds = quizSets => {
  // this is copied from pgd filter in QuizList
  const pgds = quizSets.filter(
    q => q.type === 'topic' && q.pgd === true && q.pma !== true,
  );
  const orderedPgds = orderBy(pgds, ['date'], ['desc']);
  return orderedPgds;
};

const getPmas = quizSets => {
  // this is copied from pgd filter in QuizList
  const pmas = quizSets.filter(
    q => q.type === 'topic' && q.pgd === true && q.pma === true,
  );
  const ordered = orderBy(pmas, ['date'], ['desc']);
  return ordered;
};

export const getQuizzesForTrust = (quizSets, trust, topics) => {
  const topicsForTrust = topics.filter(
    t => t.Trust === trust && t.Type === 'Topic',
  );

  const quizSetsForTopics = quizSets.filter(q => {
    for (const topic of topicsForTrust) {
      if (q.topicName === topic.PackName) {
        return true;
      }
    }
    return false;
  });

  return quizSetsForTopics;
};

export const formatDate = date => moment(date).format('D MMM YYYY');

const getTrustSubtitle = (item, data) => {
  switch (item.screenType) {
    case CONTENT_TYPES.STANDBY_CPD: {
      return `${formatDate(data[0].PublicDate)} • ${data[0].Title}`;
    }
    case CONTENT_TYPES.PGDS: {
      return `${formatDate(data[0].date)} • ${data[0].title}`;
    }
    case CONTENT_TYPES.PMAS: {
      return `${formatDate(data[0].date)} • ${data[0].title}`;
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      return `${formatDate(data[0].PublicDate)} • ${data[0].Title}`;
    }
    case CONTENT_TYPES.PODCASTS: {
      return `${formatDate(data[0].ReleaseDate)} • ${data[0].Title}`;
    }
    case CONTENT_TYPES.VIDEOS: {
      return `${formatDate(data[0].ReleaseDate)} • ${data[0].Title}`;
    }
    case CONTENT_TYPES.PSIRF: {
      const topSection = data[0]?.content.find(
        content => content.type === 'TopSection',
      );

      if (!topSection?.content) {
        return null;
      }

      return `${formatDate(topSection?.content?.DisplayDate)} • ${
        data[0].DisplayTitle
      }`;
    }
    case CONTENT_TYPES.BOOKS: {
      return data[0].Name;
    }
    case CONTENT_TYPES.QUIZZES: {
      return `${formatDate(data[0].date)} • ${data[0].title}`;
    }
    default: {
      console.log('not showing subtitle for', item.type, item, data);
      return null;
    }
  }
};

export const trustBlockOnPressHandler = (item, navigation, data) => {
  switch (item.screenType) {
    case CONTENT_TYPES.PGDS:
      return () =>
        navigation.navigate('QuizSetList', {
          quizSets: data,
          title: item.title,
          type: 'topics',
          initialFilter: 'PGD Quizzes',
          from: 'trust_pin',
        });
    case CONTENT_TYPES.PMAS:
      return () =>
        navigation.navigate('QuizSetList', {
          quizSets: data,
          title: item.title,
          type: 'topics',
          initialFilter: 'PMA Quizzes',
          from: 'trust_pin',
        });
    case CONTENT_TYPES.CASE_STUDIES:
      return () =>
        navigation.navigate('CaseStudiesDashboard', {
          caseStudies: data,
          title: item.title,
          showBackButton: true,
          from: 'trust_pin',
        });
    case CONTENT_TYPES.STANDBY_CPD:
      return () =>
        navigation.navigate('StandbyDashboard', {
          caseStudies: data,
          title: item.title,
          showBackButton: true,
          from: 'trust_pin',
        });
    case CONTENT_TYPES.PODCASTS:
      return () =>
        navigation.navigate('PodcastList', {
          title: item.title,
          data,
          from: 'trust_pin',
        });
    case CONTENT_TYPES.VIDEOS:
      return () =>
        navigation.navigate('VideoList', {
          title: item.title,
          data,
          from: 'trust_pin',
        });
    case CONTENT_TYPES.PSIRF:
      return () =>
        navigation.navigate('PSIRFDashboard', {
          data,
          title: item.title,
          from: 'trust_pin',
        });
    case CONTENT_TYPES.QUIZZES:
      return () =>
        navigation.navigate('QuizSetList', {
          quizSets: data,
          title: item.title,
          showBackButton: true,
          from: 'trust_pin',
          type: 'topics',
        });
    case CONTENT_TYPES.BOOKS:
      return () =>
        navigation.navigate('BooksDashboard', {
          data,
          title: item.title,
          from: 'trust_pin',
          showBackButton: true,
        });
    default:
      return null;
  }
};

export const getContentForType = (type, screenProps) => {
  switch (type) {
    case CONTENT_TYPES.PGDS:
      return getPgds(screenProps.quizSets);
    case CONTENT_TYPES.PMAS:
      return getPmas(screenProps.quizSets);
    case CONTENT_TYPES.CASE_STUDIES:
      return screenProps.caseStudies;
    case CONTENT_TYPES.PODCASTS:
      return screenProps.podcasts;
    case CONTENT_TYPES.VIDEOS:
      return screenProps.videos;
    case CONTENT_TYPES.PSIRF:
      return screenProps.psirfs;
    case CONTENT_TYPES.STANDBY_CPD:
      return screenProps.issues;
    case CONTENT_TYPES.BOOKS:
      return screenProps?.books;
    case CONTENT_TYPES.QUIZZES:
      return screenProps.quizSets;
    default:
      return null;
  }
};

export const getContentForFilter = (type, screenProps, dataForFilter) => {
  switch (type) {
    case CONTENT_TYPES.PGDS: {
      const names = dataForFilter.map(d => d.name);
      const pgds = getPgds(screenProps.quizSets);
      return pgds.filter(p => names.includes(p.name));
    }
    case CONTENT_TYPES.PMAS: {
      const names = dataForFilter.map(d => d.name);
      const pmas = getPmas(screenProps.quizSets);
      return pmas.filter(p => names.includes(p.name));
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      const names = dataForFilter.map(d => d.name);
      return screenProps.caseStudies.filter(c => names.includes(c.Name));
    }
    case CONTENT_TYPES.PODCASTS: {
      const ids = dataForFilter.map(d => d.id);
      return screenProps.podcasts.filter(p => ids.includes(p.RecordId));
    }
    case CONTENT_TYPES.VIDEOS: {
      const ids = dataForFilter.map(d => d.id);
      return screenProps.videos.filter(v => ids.includes(v.RecordId));
    }
    case CONTENT_TYPES.PSIRF: {
      const ids = dataForFilter.map(d => d.id);
      return screenProps.psirfs.filter(p => ids.includes(p.ID));
    }
    case CONTENT_TYPES.STANDBY_CPD: {
      const names = dataForFilter.map(d => d.name);
      const filteredIssues = screenProps.issues.filter(i =>
        names.includes(i.Name),
      );
      const quizSets = screenProps.quizSets.filter(q => q.type === 'standby');
      const quizSetIssueNames = quizSets.map(quiz => quiz.issueName);
      return filteredIssues.filter(i => quizSetIssueNames.includes(i.Name));
    }
    case CONTENT_TYPES.BOOKS: {
      const ids = dataForFilter.map(d => d.id);
      return screenProps?.books?.filter(b => ids.includes(b.ID));
    }
    case CONTENT_TYPES.QUIZZES: {
      const names = dataForFilter.map(d => d.name);
      return screenProps.quizSets.filter(p => names.includes(p.name));
    }
    default:
      return null;
  }
};

export const getContentForTrust = (type, screenProps) => {
  const userTrusts = union(
    screenProps?.user?.paraPassTrusts,
    screenProps?.user?.plusTrusts,
  );
  const trust = userTrusts[0];
  switch (type) {
    case CONTENT_TYPES.PGDS: {
      const pgds = getPgds(screenProps.quizSets);
      return pgds.filter(q => q.trust === trust);
    }
    case CONTENT_TYPES.PMAS: {
      const pmas = getPmas(screenProps.quizSets);
      return pmas.filter(q => q.trust === trust);
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      const caseStudies = screenProps.caseStudies.filter(
        c => c.Trust === trust,
      );
      return caseStudies;
    }
    case CONTENT_TYPES.PODCASTS: {
      const podcasts = screenProps.podcasts.filter(p => p.Trust === trust);
      return podcasts;
    }
    case CONTENT_TYPES.VIDEOS: {
      const videos = screenProps.videos.filter(v => v.Trust === trust);
      return videos;
    }
    case CONTENT_TYPES.PSIRF: {
      const psirfs = screenProps.psirfs.filter(p => p.Trust === trust);
      return psirfs;
    }
    case CONTENT_TYPES.STANDBY_CPD: {
      const issues = screenProps.issues.filter(i => i.Trust === trust);
      return issues;
    }
    case CONTENT_TYPES.BOOKS: {
      return screenProps?.books?.filter(b => b.Trusts.includes(trust));
    }
    case CONTENT_TYPES.QUIZZES: {
      const quizzes = getQuizzesForTrust(
        screenProps.quizSets,
        trust,
        screenProps.topics,
      );
      return quizzes;
    }
    default:
      return null;
  }
};

export const getFilteredData = (item, screenProps) => {
  const dataForFilter = item?.data?.find(d => d.type === item.screenType);

  if (!dataForFilter) {
    return null;
  }

  switch (item.contentFilter) {
    case 'Custom': {
      return getContentForFilter(
        item.screenType,
        screenProps,
        dataForFilter?.data,
      );
    }
    case 'Trust': {
      return getContentForTrust(item.screenType, screenProps);
    }
    case 'All': {
      return getContentForType(item.screenType, screenProps);
    }
    default:
      return [];
  }
};

export const getTrustBlocks = (screenProps, navigation, isRail, limit) => {
  const data = screenProps.trustBlocks
    .map((block, index) => {
      const data = getFilteredData(block, screenProps);
      if (!data?.length) return null;
      const handlePress = trustBlockOnPressHandler(block, navigation, data);
      const subtitle = getTrustSubtitle(block, data);

      return {
        ...block,
        id: `${block.id}-${index}`,
        data,
        isRail,
        onPress: handlePress,
        subtitle,
      };
    })
    .filter(Boolean);

  if (limit) {
    return data.slice(0, limit);
  }

  return data;
};

export const getLightIconForType = type => {
  switch (type) {
    case CONTENT_TYPES.CASE_STUDIES:
      return require('../assets/trustCards/casestudy-light.png');
    case CONTENT_TYPES.PODCASTS:
      return require('../assets/trustCards/podcast-light.png');
    case CONTENT_TYPES.VIDEOS:
      return require('../assets/trustCards/video-light.png');
    case CONTENT_TYPES.PSIRF:
      return require('../assets/trustCards/psirf-light.png');
    case CONTENT_TYPES.STANDBY_CPD:
      return require('../assets/trustCards/standby-light.png');
    case CONTENT_TYPES.PGDS:
      return require('../assets/trustCards/pgd-light.png');
    case CONTENT_TYPES.PMAS:
      return require('../assets/trustCards/pgd-light.png');
    case CONTENT_TYPES.QUIZZES:
      return require('../assets/trustCards/quiz-light.png');
    case CONTENT_TYPES.BOOKS:
      return require('../assets/trustCards/books-light.png');
    default:
      console.warn(`Unexpected content type: ${type}, returning null`);
      return null;
  }
};

export const getGreyIconForType = type => {
  switch (type) {
    case CONTENT_TYPES.CASE_STUDIES:
      return require('../assets/trustCards/casestudy-dark.png');
    case CONTENT_TYPES.PODCASTS:
      return require('../assets/trustCards/podcast-dark.png');
    case CONTENT_TYPES.VIDEOS:
      return require('../assets/trustCards/video-dark.png');
    case CONTENT_TYPES.PSIRF:
      return require('../assets/trustCards/psirf-dark.png');
    case CONTENT_TYPES.STANDBY_CPD:
      return require('../assets/trustCards/standby-dark.png');
    case CONTENT_TYPES.PGDS:
      return require('../assets/trustCards/pgd-dark.png');
    case CONTENT_TYPES.PMAS:
      return require('../assets/trustCards/pgd-dark.png');
    case CONTENT_TYPES.QUIZZES:
      return require('../assets/trustCards/quiz-dark.png');
    case CONTENT_TYPES.BOOKS:
      return require('../assets/trustCards/books-dark.png');
    default:
      console.warn(`Unexpected content type: ${type}, returning null`);
      return null;
  }
};

export const getLabelForType = type => {
  switch (type) {
    case CONTENT_TYPES.CASE_STUDIES:
      return 'Case Study';
    case CONTENT_TYPES.PODCASTS:
      return 'Podcast';
    case CONTENT_TYPES.VIDEOS:
      return 'Video';
    case CONTENT_TYPES.PSIRF:
      return 'PSIRF';
    case CONTENT_TYPES.STANDBY_CPD:
      return 'Standby CPD';
    case CONTENT_TYPES.PGDS:
      return 'PGD';
    case CONTENT_TYPES.PMAS:
      return 'PMA';
    case CONTENT_TYPES.QUIZZES:
      return 'Quiz';
    case CONTENT_TYPES.BOOKS:
      return 'Book';
    default:
      return type;
  }
};
